/**
 * Tara context for Suspire Terminal (/suspire-terminal/*).
 *
 * Tara should be able to answer questions about anything the Terminal has loaded:
 * the orders list, an order's tracking timeline, products, vendors, labels, B2B and
 * manual orders, dashboard metrics. Rather than wiring every screen by hand, this
 * module records the JSON that Terminal API calls return (installTerminalDataCapture
 * wraps window.fetch once, at app start) and, when the user asks Tara something,
 * buildTerminalTaraContext() turns it into a compact snapshot: the current screen
 * first, then what was loaded on other Terminal screens earlier in the session.
 *
 * Screens add what the network cannot tell — which row's modal is open, which wizard
 * step the user is on — with useTaraTerminalFocus(key, value).
 *
 * Two rules hold throughout:
 *  - Tara never sees more than the user does. Customer details are masked exactly as
 *    the Terminal masks them (vendor.hide_customer_details, for non-admins), and the
 *    dashboard figures the UI shows only to admins are dropped for everyone else.
 *  - The snapshot stays small (MAX_CONTEXT_CHARS, ~3k tokens): list rows are terse
 *    summaries, rows the question names go first, lists are shortened (saying how
 *    many rows were left out), and earlier screens contribute only the single
 *    records the user opened, not their lists. Anything else about a named order
 *    the server looks up itself (tara/terminal_context.py).
 */

import { useEffect } from 'react';
import { store } from '../app/store';

export const TERMINAL_ROUTE_PREFIX = '/suspire-terminal';
export const TERMINAL_PAGE = 'suspire_terminal';

// ~3k tokens. Every question pays for this, so it is kept deliberately small.
const MAX_CONTEXT_CHARS = 12000;
const MAX_ENTRIES = 40;
const MAX_HISTORY_ENTRIES = 3;
const MAX_STRING_CHARS = 200;
const MAX_DESCRIPTION_CHARS = 400;
const MAX_CAPTURED_ITEMS = 200;
const MAX_DEPTH = 8;
const MAX_KEYS = 60;
const MAX_FILTERS = 15;
// Last resort when even the smallest list caps leave the snapshot too big (a huge
// focus value, a product with hundreds of fields...): shrink everything in steps.
const SHRINK_STEPS = [
  { text: 120, keys: 25, items: 5, depth: 6 },
  { text: 60, keys: 12, items: 2, depth: 4 },
  { text: 40, keys: 6, items: 1, depth: 3 },
];
// Tried in order until the snapshot fits. Applies to top-level lists; lists nested
// inside an item (an order's tracking history, a product's variants) get an eighth.
const LIST_CAPS = [40, 25, 15, 10, 5, 3];
const HISTORY_LIST_CAP = 5;

const CAPTURED_PATHS = [/^\/api\/shipturtle\//, /^\/api\/dashboard\/terminal\//];
// Typeahead, reference data and job polling: noise for Tara.
const IGNORED_PATHS = [
  /\/orders\/search-customers\//,
  /\/orders\/search-tags\//,
  /\/orders\/filter-options\//,
  /\/orders\/carriers\//,
  /\/terminal-banners\//,
  /\/exports?\//,
  /\/categories\//,
  /\/variant-options\//,
  /\/sync-field-filters\//,
  /\/webhooks\//,
];
const ROLE_PATH = /^\/api\/shipturtle\/(v2\/)?user\/role\/$/;
// Pagination and ordering do not change *what* a list is, so a later page or a
// re-sort replaces the earlier capture instead of sitting beside it.
const VOLATILE_PARAMS = new Set([
  'limit', 'offset', 'page', 'page_size', 'sort', 'sort_field', 'sort_direction',
  'ordering', '_', 't', 'ts',
]);

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const ORDER_LIST_PATH = /^\/api\/shipturtle\/orders\/$/;
const ORDER_DETAIL_PATH = new RegExp(`^/api/shipturtle/orders/(${UUID})/$`, 'i');
const PRODUCT_LIST_PATH = /^\/api\/shipturtle\/v2\/products\/$/;
const PRODUCT_DETAIL_PATH = new RegExp(`^/api/shipturtle/v2/products/(${UUID})/$`, 'i');
const DASHBOARD_PATH = /^\/api\/dashboard\/terminal\//;
const ORDER_COUNTS_PATH = /^\/api\/shipturtle\/orders\/counts\/$/;
// Single records (an order, product, vendor...). Only these carry over from earlier
// screens; lists from those screens would cost a lot for little.
const RECORD_PATH = new RegExp(`/(${UUID}|[A-Za-z0-9_-]*\\d[A-Za-z0-9_-]*)/(history/)?$`, 'i');
const UUID_VALUE = new RegExp(`^${UUID}$`, 'i');

// Keys never worth a token: binary/presigned links, raw carrier payloads, secrets.
// Checked after KEPT_KEYS, so tracking links survive.
const KEPT_KEYS = new Set(['tracking_link', 'tracking_url']);
const DROPPED_KEY = /^(raw_.*|.*_url|url|urls|image|images|.*_image|media|logo|thumbnail.*|src|.*token.*|.*secret.*|.*password.*|api_key|access_key|signature|ip_address|user_agent|content_key|qc_security_key|success)$/i;
// Customer details in data that has no order summarizer. Only admins see these in
// the Terminal (label, exception, manual and B2B screens are admin-only), so for
// anyone else they are masked wholesale.
const CUSTOMER_KEY = /^(customer|customers|customer_[a-z_]+|shipping_address|billing_address|ship_to_[a-z_]+|consignee[a-z_]*)$/i;
// What TerminalDashboard.js shows a vendor, per dashboard endpoint ("a.b" nested
// field, "a[].b" field of each row, "a.*" any field under a). For non-admins every
// other field is dropped, and an endpoint not listed here (vendor-performance is
// admin-only) gives nothing: fail closed, so a new admin-only figure never reaches
// Tara for vendors by accident.
const VENDOR_DASHBOARD_FIELDS = {
  'terminal/': [
    'overview.total_orders', 'overview.avg_order_value', 'overview.rto_orders',
    'overview.delivery_rate', 'overview.delivered_orders', 'overview.cancelled_orders',
    'overview.cod_count', 'overview.prepaid_count', 'daily_orders[].date',
    'daily_orders[].orders', 'daily_orders[].revenue', 'peak_day.date', 'status_breakdown.*',
  ],
  'terminal/performance/': [
    'summary.total_orders', 'summary.avg_order_value', 'summary.delivered',
    'summary.in_transit', 'summary.confirmed', 'summary.ready_to_ship', 'summary.rto_count',
    'summary.cancelled_count', 'summary.cod_count', 'summary.prepaid_count',
    'status_breakdown.*', 'orders[].order_number', 'orders[].vendor', 'orders[].products',
    'orders[].items', 'orders[].value', 'orders[].payment', 'orders[].status',
  ],
  'terminal/product-performance/': [
    'summary.total_unique_products', 'summary.total_qty_ordered', 'sku_breakdown[].sku',
    'sku_breakdown[].name', 'sku_breakdown[].orders', 'sku_breakdown[].qty',
  ],
  'terminal/shipping-performance/': [
    'summary.total_shipments', 'summary.pending', 'summary.pending_pct', 'summary.rto',
    'summary.awb_assigned', 'partners_chart[].partner', 'partners_chart[].count',
    'partners_chart[].color', 'status_donut[].label', 'status_donut[].value',
    'status_donut[].color', 'inflight_orders[].date', 'inflight_orders[].order_number',
    'inflight_orders[].vendor', 'inflight_orders[].awb', 'inflight_orders[].partner',
    'inflight_orders[].tracking_link', 'inflight_orders[].status',
  ],
  'terminal/geographic/': [
    'total_states', 'total_cities', 'top_state.state', 'top_state.orders', 'top_city.city',
    'top_city.orders', 'states[].rank', 'states[].state', 'states[].orders', 'states[].pct',
    'cities[].city', 'cities[].state', 'cities[].orders',
  ],
  'terminal/order-ageing/': [
    'summary.total_orders', 'summary.avg_age_days', 'summary.active_orders',
    'summary.critical_count', 'buckets[].label', 'buckets[].orders', 'buckets[].line_items',
    'buckets[].pct', 'buckets[].statuses', 'critical_orders[].order_number',
    'critical_orders[].vendor', 'critical_orders[].status', 'critical_orders[].created_at',
    'critical_orders[].age_days', 'critical_orders[].value',
  ],
  'terminal/shipment-ageing/': [
    'summary.active_shipments', 'summary.avg_age_days', 'summary.critical_count',
    'summary.ready_to_ship', 'summary.in_transit', 'buckets[].label', 'buckets[].orders',
    'buckets[].line_items', 'buckets[].pct', 'buckets[].action', 'buckets[].is_critical',
    'critical_orders[].order_number', 'critical_orders[].vendor', 'critical_orders[].status',
    'critical_orders[].created_at', 'critical_orders[].age_days', 'critical_orders[].awb',
    'critical_orders[].partner', 'critical_orders[].tracking_link',
  ],
  'terminal/delivered-ageing/': [
    'kpis.total_delivered', 'kpis.with_ageing_data', 'kpis.avg_delivered_age', 'kpis.vendors',
    'breakdown[].label', 'breakdown[].orders', 'breakdown[].line_items', 'breakdown[].pct',
    'vendor_ageing[].vendor', 'vendor_ageing[].delivered_orders', 'vendor_ageing[].avg_ageing',
  ],
  'terminal/delivery-timeline/': [
    'kpis.total_delivered', 'kpis.avg_delivery_days', 'kpis.fastest_delivery',
    'kpis.slowest_delivery', 'breakdown[].label', 'breakdown[].orders', 'breakdown[].pct',
    'breakdown[].performance', 'vendor_timeline[].vendor', 'vendor_timeline[].orders',
    'vendor_timeline[].avg_days', 'vendor_timeline[].min_days', 'vendor_timeline[].max_days',
  ],
  'terminal/cancellation/': [
    'kpis.total_cancellations', 'kpis.cancel_rate', 'reason_breakdown[].reason',
    'reason_breakdown[].orders', 'reason_breakdown[].line_items', 'reason_breakdown[].revenue',
    'reason_breakdown[].pct', 'vendor_breakdown[].vendor',
    'vendor_breakdown[].cancelled_orders', 'vendor_breakdown[].total_orders',
    'vendor_breakdown[].cancel_rate', 'daily_trend[].date', 'daily_trend[].count',
  ],
};
const HIDDEN = 'hidden';
const MORE_MARKER = /^\(\+(\d+) more not shown\)$/;
// Marks objects built by summarizeOrder, whose customer block is masked by the
// order's own hide flag. A symbol, so it never reaches the serialized snapshot.
const ORDER_SUMMARY = Symbol('taraOrderSummary');

const ORDER_STATUS_LABELS = {
  new: 'New',
  confirmed: 'Confirmed',
  label_generated: 'Label Generated',
  ready_to_ship: 'Ready to Ship',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  exceptions: 'Exceptions',
  rto: 'Return to Origin (RTO)',
  held: 'Held',
  cancelled: 'Cancelled',
  lost: 'Lost',
  damaged: 'Damaged',
};

const SCREENS = [
  { pattern: /^\/suspire-terminal\/?(dashboard)?\/?$/, name: 'Dashboard' },
  { pattern: new RegExp(`^/suspire-terminal/orders/(${UUID})/?$`, 'i'), name: 'Order details', focus: 'order' },
  { pattern: /^\/suspire-terminal\/orders\/?$/, name: 'Orders' },
  { pattern: /^\/suspire-terminal\/labels\/?$/, name: 'Shipping labels' },
  { pattern: /^\/suspire-terminal\/manual-orders\/new\/?$/, name: 'Create manual order' },
  { pattern: /^\/suspire-terminal\/b2b-orders\/new\/?$/, name: 'Create B2B order' },
  { pattern: /^\/suspire-terminal\/b2b-orders\/[^/]+\/edit\/?$/, name: 'Edit B2B order' },
  { pattern: /^\/suspire-terminal\/b2b-orders\/?$/, name: 'B2B orders' },
  { pattern: /^\/suspire-terminal\/products(\/manage)?\/?$/, name: 'Products' },
  { pattern: /^\/suspire-terminal\/products\/manage\/[^/]+\/?$/, name: 'Product details' },
  { pattern: /^\/suspire-terminal\/products\/pending\/?$/, name: 'Products pending approval' },
  { pattern: /^\/suspire-terminal\/products\/pending\/[^/]+\/?$/, name: 'Product approval review' },
  { pattern: /^\/suspire-terminal\/products\/unlisted\/?$/, name: 'Unlisted products' },
  { pattern: /^\/suspire-terminal\/products\/unlisted\/[^/]+\/?$/, name: 'Unlisted product details' },
  { pattern: /^\/suspire-terminal\/products\/add\/manual\/?$/, name: 'Add product' },
  { pattern: /^\/suspire-terminal\/products\/add\/bulk\/?$/, name: 'Bulk upload products' },
  { pattern: /^\/suspire-terminal\/products\/add\/?$/, name: 'Add products' },
  { pattern: /^\/suspire-terminal\/vendors\/?$/, name: 'Vendors' },
  { pattern: /^\/suspire-terminal\/vendor-store\/product\/[^/]+\/?$/, name: 'Vendor store product' },
  { pattern: /^\/suspire-terminal\/vendor-store\/?$/, name: 'Vendor store' },
  { pattern: /^\/suspire-terminal\/faq\/?$/, name: 'FAQ & resources' },
];

const SOURCES = [
  [ORDER_LIST_PATH, 'Orders list'],
  [/^\/api\/shipturtle\/orders\/counts\/$/, 'Order counts by status'],
  [ORDER_DETAIL_PATH, 'Order details'],
  [/^\/api\/shipturtle\/orders\/exception-labels\/$/, 'Return & replacement labels'],
  [/^\/api\/shipturtle\/orders\/label-search\/$/, 'Order search (labels)'],
  [/\/label-context\/$/, 'Label form details for an order'],
  [/^\/api\/shipturtle\/orders\/manual\/$/, 'Manual orders'],
  [/^\/api\/shipturtle\/orders\/manual\/[^/]+\/$/, 'Manual order details'],
  [/^\/api\/shipturtle\/orders\/b2b\/$/, 'B2B orders'],
  [/^\/api\/shipturtle\/orders\/b2b\/[^/]+\/history\/$/, 'B2B order history'],
  [/^\/api\/shipturtle\/orders\/b2b\/[^/]+\/$/, 'B2B order details'],
  [/^\/api\/shipturtle\/notes\//, 'Notes'],
  [PRODUCT_LIST_PATH, 'Products list'],
  [/\/products\/[^/]+\/audit-history\/$/, 'Product change history'],
  [/\/products\/[^/]+\/review-summary\/$/, 'Product review summary'],
  [PRODUCT_DETAIL_PATH, 'Product details'],
  [/\/connected-store\/products\//, 'Vendor store products'],
  [/^\/api\/shipturtle\/vendors\/counts\/$/, 'Vendor counts'],
  [/^\/api\/shipturtle\/vendors\/$/, 'Vendors list'],
  [/^\/api\/shipturtle\/vendors\/[^/]+\/$/, 'Vendor details'],
  [/^\/api\/shipturtle\/vendors\//, 'Vendor settings'],
  [/^\/api\/shipturtle\/faq\//, 'FAQ documents'],
];

const state = {
  entries: new Map(), // key -> { key, path, filters, route, visit, at, data }
  focus: new Map(), // key -> { route, value }
  viewer: null, // { role, vendor_id, vendor_name } from /user/role/
  ownerId: undefined,
  visit: 0,
  visitPath: null,
};

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

export const isTerminalRoute = (pathname) =>
  typeof pathname === 'string' && pathname.startsWith(TERMINAL_ROUTE_PREFIX);

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

const isEmpty = (v) =>
  v === null || v === undefined || v === '' ||
  (Array.isArray(v) && v.length === 0) ||
  (isPlainObject(v) && Object.keys(v).length === 0);

// Drops empty values, so absent data costs nothing.
const compact = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (!isEmpty(v)) out[k] = v;
  });
  return out;
};

const toNumber = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const cleanText = (value, max = MAX_STRING_CHARS) => {
  if (typeof value !== 'string') return value;
  let text = value;
  if (text.includes('<')) text = text.replace(/<[^>]*>/g, ' ');
  text = text.replace(/\s+/g, ' ').trim();
  // Base64 blobs and data URIs: long, no spaces.
  if (text.length > 200 && !/\s/.test(text)) return undefined;
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

// Readable IST, so Tara never has to convert time zones itself. Date-only values
// ("2026-10-01") pass through: they have no time to convert.
const istTime = (value) => {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// String, so "42" from localStorage and 42 from the login response are one user.
const currentUserId = () => {
  try {
    const id = store.getState()?.user?.user_id;
    return id === null || id === undefined || id === '' ? null : String(id);
  } catch {
    return null;
  }
};

const truncate = (text, max) => (typeof text === 'string' && text.length > max
  ? `${text.slice(0, max)}…`
  : text);

// Captured data belongs to whoever was signed in; drop it when that changes.
const ensureOwner = () => {
  const userId = currentUserId();
  if (state.ownerId !== undefined && state.ownerId !== userId) {
    state.entries.clear();
    state.focus.clear();
    state.viewer = null;
  }
  state.ownerId = userId;
};

// A "visit" is an uninterrupted stay on one Terminal screen (pathname). Tab, filter
// and page changes inside a screen keep the visit; moving to another screen ends it.
const syncVisit = () => {
  const pathname = window.location.pathname;
  if (pathname !== state.visitPath) {
    state.visit += 1;
    state.visitPath = pathname;
  }
};

const apiPathOf = (url) => {
  const index = url.pathname.indexOf('/api/');
  return index >= 0 ? url.pathname.slice(index) : null;
};

const filtersOf = (url) => {
  const filters = {};
  url.searchParams.forEach((value, name) => {
    if (VOLATILE_PARAMS.has(name) || value === '' || Object.keys(filters).length >= MAX_FILTERS) return;
    filters[truncate(name, 60)] = truncate(value, MAX_STRING_CHARS);
  });
  return filters;
};

// ---------------------------------------------------------------------------
// Normalising captured responses (role-independent; masking happens at build time)
// ---------------------------------------------------------------------------

/** Generic pruning: drops noise keys, empties and long text; bounds lists and depth. */
export const prune = (value, depth = 0) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return cleanText(value);
  if (typeof value !== 'object') return value;
  if (depth >= MAX_DEPTH) return undefined;

  if (Array.isArray(value)) {
    const items = value
      .slice(0, MAX_CAPTURED_ITEMS)
      .map((item) => prune(item, depth + 1))
      .filter((item) => !isEmpty(item));
    if (value.length > MAX_CAPTURED_ITEMS) {
      items.push(`(+${value.length - MAX_CAPTURED_ITEMS} more not shown)`);
    }
    return items;
  }

  const out = {};
  let skipped = 0;
  Object.entries(value).forEach(([key, v]) => {
    if (!KEPT_KEYS.has(key) && DROPPED_KEY.test(key)) return;
    // Internal ids mean nothing to the user and cost ~10 tokens each.
    if (typeof v === 'string' && UUID_VALUE.test(v)) return;
    const pruned = prune(v, depth + 1);
    if (isEmpty(pruned)) return;
    if (Object.keys(out).length >= MAX_KEYS) {
      skipped += 1;
      return;
    }
    out[truncate(key, 60)] = pruned;
  });
  if (skipped) out['(more fields)'] = skipped;
  return out;
};

const summarizeTrackingEvent = (event) => compact({
  at: istTime(event.event_timestamp),
  status: event.mapped_status,
  location: cleanText(event.location),
  detail: cleanText(event.description),
  reason: event.reason_label,
});

/**
 * One order as Tara needs it: what it is, where it is, and how it got there.
 * Customer details are kept here, with the vendor's hide flag beside them, and
 * masked in buildTerminalTaraContext once the viewer's role is known.
 */
export const summarizeOrder = (order, { detail = false } = {}) => {
  if (!isPlainObject(order)) return undefined;
  const summary = detail ? summarizeOrderFields(order, true) : summarizeOrderRow(order);
  summary[ORDER_SUMMARY] = true;
  return summary;
};

const istDate = (value) => istTime(value)?.replace(/,.*$/, '');

const eventLine = (event) => (isPlainObject(event)
  ? [cleanText(event.description), event.reason_label, cleanText(event.location), istTime(event.event_timestamp)]
    .filter(Boolean).join(' · ')
  : undefined);

// One line of a list: enough to say where each order is, nothing more. The order
// details screen and the server lookup carry everything else.
const summarizeOrderRow = (order) => {
  const shipment = isPlainObject(order.shipment) ? order.shipment : null;
  const events = (shipment?.tracking_events || []).filter(isPlainObject);
  const latest = events.reduce((a, b) => (
    !a || String(b.event_timestamp) > String(a.event_timestamp) ? b : a), null);
  const vendor = isPlainObject(order.vendor) ? order.vendor : {};
  const items = (order.line_items || []).filter(isPlainObject);
  return compact({
    order_number: order.order_number,
    status: ORDER_STATUS_LABELS[order.status] || order.status,
    status_reason: order.reason_label,
    placed: istDate(order.created_at),
    value: `${toNumber(order.total_price) - toNumber(order.suspire_discount_amount)}`,
    payment: order.payment_method,
    vendor: vendor.brand_name || vendor.company_name,
    hide_customer_details: Boolean(vendor.hide_customer_details) || undefined,
    customer: compact({ name: order.customer?.full_name, city: order.shipping_address?.city }),
    items: items.slice(0, 2).map((li) => `${cleanText(li.product_name, 40)} x${li.quantity}`).join('; ')
      + (items.length > 2 ? ` (+${items.length - 2} more)` : ''),
    awb: shipment?.awb_number,
    courier: shipment?.carrier?.name,
    latest_scan: latest ? eventLine(latest) : undefined,
    follow_up: order.follow_up_stage && order.follow_up_stage !== 'none' ? order.follow_up_stage : undefined,
  });
};

const summarizeOrderFields = (order, detail) => {
  const shipment = isPlainObject(order.shipment) ? order.shipment : null;
  const events = (shipment?.tracking_events || [])
    .filter(isPlainObject)
    .slice()
    .sort((a, b) => String(a.event_timestamp).localeCompare(String(b.event_timestamp)));
  const latest = events[events.length - 1];
  const address = (a) => (isPlainObject(a)
    ? [a.address_line_1, a.address_line_2, a.landmark, a.city, a.state, a.pincode, a.country]
      .filter(Boolean).join(', ')
    : undefined);
  const meta = isPlainObject(order.meta) ? order.meta : {};
  const vendor = isPlainObject(order.vendor) ? order.vendor : {};
  const netValue = toNumber(order.total_price) - toNumber(order.suspire_discount_amount);

  return compact({
    order_number: order.order_number,
    status: ORDER_STATUS_LABELS[order.status] || order.status,
    status_reason: order.reason_label,
    placed_at: istTime(order.created_at),
    confirmed_at: istTime(order.confirmed_at),
    delivered_at: istTime(order.delivered_at),
    cancelled_at: istTime(meta.cancelled_at),
    cancellation_reason: cleanText(meta.cancellation_reason),
    cancellation_notes: cleanText(meta.cancellation_reason_notes || meta.cancellation_notes),
    order_value: `${order.currency || 'INR'} ${netValue.toFixed(2)}`,
    payment: compact({ method: order.payment_method, status: order.financial_status }),
    vendor: vendor.brand_name || vendor.company_name,
    hide_customer_details: Boolean(vendor.hide_customer_details) || undefined,
    customer: compact({
      name: order.customer?.full_name,
      email: order.customer?.email,
      phone: order.customer?.phone,
      ship_to: detail ? address(order.shipping_address) : [order.shipping_address?.city, order.shipping_address?.state].filter(Boolean).join(', '),
      bill_to: detail ? address(order.billing_address) : undefined,
    }),
    pickup_from: order.pickup_address?.warehouse_name,
    items: (order.line_items || []).filter(isPlainObject).map((li) => compact({
      product: cleanText(li.product_name),
      sku: li.sku,
      qty: li.quantity,
      price: li.price,
    })),
    shipment: shipment ? compact({
      awb: shipment.awb_number,
      courier: shipment.carrier?.name,
      shipping_partner: shipment.shipping_partner,
      label_generated_at: istTime(shipment.label_generated_at),
      ready_to_ship_at: istTime(shipment.rts_at),
      picked_up_at: istTime(shipment.first_scanned_at),
      delivered_at: istTime(shipment.delivered_at),
      manifest_pending: shipment.manifest_pending || undefined,
      exception: isPlainObject(shipment.exception_info) ? compact({
        status: shipment.exception_info.mapped_status,
        reason: shipment.exception_info.reason_label,
        location: shipment.exception_info.location,
        at: istTime(shipment.exception_info.event_timestamp),
      }) : undefined,
      latest_update: latest ? summarizeTrackingEvent(latest) : undefined,
    }) : undefined,
    tracking_link: order.tracking_link,
    // Lists keep only latest_update (above) so a full page of orders fits; the
    // order details screen carries the whole journey.
    tracking_history: detail ? events.map(summarizeTrackingEvent) : undefined,
    invoice: order.invoice?.has_active ? order.invoice.invoice_number || 'generated' : undefined,
    follow_up: order.follow_up_stage && order.follow_up_stage !== 'none'
      ? compact({ stage: order.follow_up_stage, note: cleanText(order.exception_note) })
      : undefined,
    tags: Array.isArray(order.tags?.tags) ? order.tags.tags : undefined,
    notes: cleanText(order.notes),
    vendor_store_sync: order.vendor_shopify_sync?.status,
    activity: detail
      ? (order.audit_logs || []).filter(isPlainObject).map((log) => compact({
        at: istTime(log.created_at),
        event: String(log.action || '').replace(/_/g, ' '),
        by_role: log.user_role,
        from_status: log.payload_snapshot?.old_status || log.payload_snapshot?.previous_status,
        to_status: log.payload_snapshot?.new_status,
      })).reverse()
      : undefined,
  });
};

export const summarizeProduct = (product, { detail = false } = {}) => {
  if (!isPlainObject(product)) return undefined;
  const variants = (product.variants || []).filter(isPlainObject);
  const prices = variants.map((v) => toNumber(v.price)).filter((p) => p > 0);
  if (!detail) {
    return compact({
      title: cleanText(product.title, 80),
      status: product.terminal_status,
      vendor: product.vendor?.brand_name || product.vendor?.company_name || product.vendor?.name,
      category: product.category?.name,
      price: prices.length ? `${Math.min(...prices)}${Math.max(...prices) !== Math.min(...prices) ? `-${Math.max(...prices)}` : ''}` : undefined,
      inventory: variants.some((v) => v.inventory_quantity !== undefined && v.inventory_quantity !== null)
        ? variants.reduce((sum, v) => sum + toNumber(v.inventory_quantity), 0)
        : undefined,
      variants: variants.length > 1 ? variants.length : undefined,
      skus: variants.slice(0, 3).map((v) => v.sku).filter(Boolean).join(', ') || undefined,
    });
  }
  return compact({
    title: cleanText(product.title),
    status: product.terminal_status,
    state: product.state,
    vendor: product.vendor?.brand_name || product.vendor?.company_name || product.vendor?.name,
    category: product.category?.name,
    tags: product.tags,
    price: prices.length
      ? (Math.min(...prices) === Math.max(...prices)
        ? `${Math.min(...prices)}`
        : `${Math.min(...prices)} - ${Math.max(...prices)}`)
      : undefined,
    total_inventory: variants.some((v) => v.inventory_quantity !== undefined && v.inventory_quantity !== null)
      ? variants.reduce((sum, v) => sum + toNumber(v.inventory_quantity), 0)
      : undefined,
    variants: variants.map((v) => compact({
      sku: v.sku,
      name: cleanText(v.variant_name || v.name),
      price: v.price,
      compare_price: v.compare_price,
      inventory: v.inventory_quantity,
      weight_kg: detail ? v.weight_kg : undefined,
      gst_rate: detail ? v.gst_rate : undefined,
    })),
    stores: (product.store_mappings || []).filter(isPlainObject).map((m) => compact({
      platform: m.platform,
      owner: m.owner_type,
      status: m.external_status,
      mapped: m.is_mapped,
      last_synced_at: istTime(m.last_synced_at),
    })),
    published_at: istTime(product.published_at),
    updated_at: istTime(product.updated_at),
    description: detail ? cleanText(product.description, MAX_DESCRIPTION_CHARS) : undefined,
    details: detail ? prune(product.meta) : undefined,
  });
};

const normalizeResponse = (path, data) => {
  if (ORDER_COUNTS_PATH.test(path) && isPlainObject(data?.status_counts)) {
    return Object.fromEntries(Object.values(data.status_counts)
      .filter((c) => isPlainObject(c) && c.count)
      .map((c) => [c.label, c.count]));
  }
  if (ORDER_LIST_PATH.test(path) && Array.isArray(data?.orders)) {
    return compact({
      total: data.count,
      offset: data.offset || undefined,
      orders: data.orders.slice(0, MAX_CAPTURED_ITEMS).map((o) => summarizeOrder(o)),
    });
  }
  if (ORDER_DETAIL_PATH.test(path) && isPlainObject(data?.order)) {
    return { order: summarizeOrder(data.order, { detail: true }) };
  }
  if (PRODUCT_LIST_PATH.test(path) && Array.isArray(data?.products)) {
    return compact({
      total: data.count,
      offset: data.offset || undefined,
      products: data.products.slice(0, MAX_CAPTURED_ITEMS).map((p) => summarizeProduct(p)),
    });
  }
  if (PRODUCT_DETAIL_PATH.test(path) && isPlainObject(data?.product)) {
    return compact({
      product: summarizeProduct(data.product, { detail: true }),
      missing_fields: prune(data.missing_fields),
    });
  }
  return prune(data);
};

// ---------------------------------------------------------------------------
// Capture
// ---------------------------------------------------------------------------

const apiOrigin = (() => {
  try {
    return process.env.REACT_APP_API_BASE_URL
      ? new URL(process.env.REACT_APP_API_BASE_URL).origin
      : null;
  } catch {
    return null;
  }
})();

/**
 * Decides, when a request starts, whether its response should be recorded.
 * Returns the capture details, or null. Only GETs to the Terminal's own APIs,
 * made while a Terminal screen is open, qualify.
 */
const beginCapture = (input, init) => {
  // Before the route check, so leaving the Terminal and coming back is a new visit.
  syncVisit();
  if (!isTerminalRoute(window.location.pathname)) return null;
  const method = String(init?.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
  if (method !== 'GET') return null;

  let url;
  try {
    url = new URL(typeof input === 'string' ? input : input?.url || String(input), window.location.origin);
  } catch {
    return null;
  }
  if (apiOrigin && url.origin !== apiOrigin && url.origin !== window.location.origin) return null;
  const path = apiPathOf(url);
  if (!path || !CAPTURED_PATHS.some((re) => re.test(path))) return null;
  if (IGNORED_PATHS.some((re) => re.test(path))) return null;
  // Count probes (limit=1) would otherwise replace the list they count.
  if (url.searchParams.get('limit') === '1' || url.searchParams.get('page_size') === '1') return null;

  ensureOwner();
  return {
    path,
    filters: filtersOf(url),
    route: window.location.pathname,
    visit: state.visit,
    owner: state.ownerId,
  };
};

const recordTerminalResponse = (capture, data) => {
  if (!capture || data === undefined) return;
  ensureOwner();
  // The signed-in user changed while this was in flight: it is not theirs.
  if (capture.owner !== state.ownerId) return;

  if (ROLE_PATH.test(capture.path)) {
    if (isPlainObject(data) && data.role) {
      state.viewer = { role: data.role, vendor_id: data.vendor_id, vendor_name: data.vendor_name };
    }
    return;
  }

  const normalized = normalizeResponse(capture.path, data);
  if (isEmpty(normalized)) return;

  const filterKey = Object.keys(capture.filters).sort()
    .map((k) => `${k}=${capture.filters[k]}`).join('&');
  const key = `${capture.path}?${filterKey}`;
  state.entries.delete(key); // re-insert so Map order stays oldest -> newest
  state.entries.set(key, {
    key,
    path: capture.path,
    filters: capture.filters,
    route: capture.route,
    visit: capture.visit,
    at: Date.now(),
    data: normalized,
  });
  while (state.entries.size > MAX_ENTRIES) {
    state.entries.delete(state.entries.keys().next().value);
  }
};

const captureFromResponse = (capture, response) => {
  if (!response?.ok || typeof response.clone !== 'function') return;
  const type = response.headers?.get?.('content-type') || '';
  if (!type.includes('application/json')) return; // label PDFs, CSV exports, downloads
  let copy;
  try {
    copy = response.clone();
  } catch {
    return;
  }
  copy.json().then((data) => recordTerminalResponse(capture, data)).catch(() => {});
};

let installed = false;

/**
 * Wraps window.fetch so Terminal API responses are recorded for Tara. Idempotent.
 * The caller always gets the original response; recording works on a clone and
 * can never fail the request.
 */
export const installTerminalDataCapture = () => {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;
  const originalFetch = window.fetch;

  window.fetch = function taraTerminalFetch(input, init) {
    let capture = null;
    try {
      capture = beginCapture(input, init);
    } catch {
      capture = null;
    }
    const request = originalFetch.call(window, input, init);
    if (!capture) return request;
    return request.then((response) => {
      try {
        captureFromResponse(capture, response);
      } catch {
        // Recording is best effort; the request itself is untouched.
      }
      return response;
    });
  };
};

// ---------------------------------------------------------------------------
// Screen focus (state the network can't reveal)
// ---------------------------------------------------------------------------

/**
 * Tells Tara what the user has open on the current screen, e.g. the B2B order whose
 * modal is showing or the wizard step. Pass null when nothing is open. Cleared on
 * unmount. Keep the value small: identifiers and labels, not whole records.
 */
export const useTaraTerminalFocus = (key, value) => {
  let serialized = null;
  try {
    serialized = value === null || value === undefined ? null : JSON.stringify(value);
  } catch {
    serialized = null;
  }
  useEffect(() => {
    if (serialized === null) {
      state.focus.delete(key);
      return undefined;
    }
    ensureOwner(); // a user switch clears the store before, not after, this write
    state.focus.set(key, { route: window.location.pathname, value: JSON.parse(serialized) });
    return () => {
      state.focus.delete(key);
    };
  }, [key, serialized]);
};

// ---------------------------------------------------------------------------
// Building the snapshot
// ---------------------------------------------------------------------------

const describeScreen = (pathname) => SCREENS.find((s) => s.pattern.test(pathname));

const sourceLabel = (path) => {
  const match = SOURCES.find(([re]) => re.test(path));
  if (match) return match[1];
  if (DASHBOARD_PATH.test(path)) {
    const section = path.replace(DASHBOARD_PATH, '').replace(/\/$/, '').replace(/-/g, ' ');
    return `Dashboard: ${section || 'overview'}`;
  }
  return path.replace(new RegExp(UUID, 'gi'), ':id');
};

// What the URL says about the screen: tab, section, search, filters, page.
const viewFromUrl = () => {
  const view = {};
  const read = (params) => params.forEach((value, name) => {
    if (value === '') return;
    try {
      view[name] = /^[[{]/.test(value) ? JSON.parse(value) : value;
    } catch {
      view[name] = value;
    }
  });
  read(new URLSearchParams(window.location.search));
  const hash = window.location.hash.replace(/^#/, '');
  if (hash) read(new URLSearchParams(hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : hash));
  return shrink(view, SHRINK_STEPS[0]);
};

// JSON-only shrinking for the last-resort paths: shorter strings, fewer keys and
// items (merging "(+N more not shown)" markers), shallower nesting.
const shrink = (value, step, depth = 0) => {
  if (typeof value === 'string') return truncate(value, step.text);
  if (value === null || typeof value !== 'object') return value;
  if (depth >= step.depth) return '(nested data not shown)';
  if (Array.isArray(value)) {
    let left = 0;
    const real = value.filter((item) => {
      const marker = typeof item === 'string' && item.match(MORE_MARKER);
      if (marker) left += Number(marker[1]);
      return !marker;
    });
    const kept = real.slice(0, step.items).map((item) => shrink(item, step, depth + 1));
    left += real.length - kept.length;
    if (left > 0) kept.push(`(+${left} more not shown)`);
    return kept;
  }
  const entries = Object.entries(value);
  const out = {};
  entries.slice(0, step.keys).forEach(([key, v]) => {
    out[truncate(key, 60)] = shrink(v, step, depth + 1);
  });
  if (entries.length > step.keys) out['(more fields)'] = entries.length - step.keys;
  return out;
};

// Same rule as utils/customerMask.js: hidden when the vendor hides customer details
// and the viewer is not an admin.
const maskCustomer = (order, isAdmin) => {
  if (isAdmin || !order.hide_customer_details || !order.customer) return order;
  return {
    ...order,
    customer: Object.fromEntries(Object.keys(order.customer).map((k) => [k, HIDDEN])),
  };
};

// Masks and bounds one captured payload for the viewer. `cap` bounds top-level lists.
// Whether a dashboard field at `path` ("summary.total_orders", "orders[].status")
// is one the Terminal shows vendors: listed itself, under a listed "x.*", or a
// container of a listed field.
const vendorMaySee = (fields, path) => fields.some((field) => field === path
  || field.startsWith(`${path}.`)
  || field.startsWith(`${path}[]`)
  || (field.endsWith('.*') && path.startsWith(field.slice(0, -1))));

const prepare = (entry, { isAdmin, cap, refs = [] }) => {
  const nestedCap = Math.max(2, Math.floor(cap / 8));
  // Rows naming something the question mentions (an order number, AWB, SKU) go
  // first, so shortening a list never drops what was asked about.
  const mentioned = (item) => refs.length > 0 && isPlainObject(item)
    && refs.some((ref) => JSON.stringify(item).toUpperCase().includes(ref));
  // Vendors get only the dashboard fields their dashboard shows them; an endpoint
  // missing from VENDOR_DASHBOARD_FIELDS gives them nothing (fail closed).
  const dashboardFields = !isAdmin && DASHBOARD_PATH.test(entry.path)
    ? VENDOR_DASHBOARD_FIELDS[entry.path.replace(/^\/api\/dashboard\//, '')] || []
    : null;

  const walk = (value, depth, path) => {
    if (Array.isArray(value)) {
      const limit = depth <= 2 ? cap : nestedCap;
      let left = 0;
      const real = value.filter((item) => {
        const marker = typeof item === 'string' && item.match(MORE_MARKER);
        if (marker) left += Number(marker[1]);
        return !marker;
      });
      const ordered = depth <= 2 && refs.length
        ? [...real.filter(mentioned), ...real.filter((item) => !mentioned(item))]
        : real;
      const items = ordered.slice(0, limit).map((item) => walk(item, depth + 1, `${path}[]`));
      left += real.length - items.length;
      if (left > 0) items.push(`(+${left} more not shown)`);
      return items;
    }
    if (!isPlainObject(value)) return value;

    const isOrderSummary = Boolean(value[ORDER_SUMMARY]);
    const source = isOrderSummary ? maskCustomer(value, isAdmin) : value;
    const out = {};
    Object.entries(source).forEach(([key, v]) => {
      const keyPath = path ? `${path}.${key}` : key;
      if (dashboardFields && !vendorMaySee(dashboardFields, keyPath)) return;
      if (!isAdmin && !isOrderSummary && CUSTOMER_KEY.test(key)) {
        out[key] = HIDDEN;
        return;
      }
      out[key] = walk(v, depth + 1, keyPath);
    });
    return out;
  };

  return walk(entry.data, 0, '');
};

const formatEntry = (entry, options) => compact({
  source: sourceLabel(entry.path),
  filters: isEmpty(entry.filters) ? undefined : entry.filters,
  screen: options.withScreen ? describeScreen(entry.route)?.name || entry.route : undefined,
  loaded_at: istTime(entry.at),
  data: prepare(entry, options),
});

const assemble = ({ cap, historyLimit, pathname, isAdmin, focusOrderId, refs }) => {
  const screen = describeScreen(pathname);
  const all = Array.from(state.entries.values()).reverse(); // newest first
  const current = all.filter((e) => e.route === pathname && e.visit === state.visit);
  const history = all
    .filter((e) => !(e.route === pathname && e.visit === state.visit) && RECORD_PATH.test(e.path))
    .slice(0, historyLimit);

  // The order the screen is about (order details route) goes first.
  if (focusOrderId) {
    current.sort((a, b) => Number(b.path.includes(focusOrderId)) - Number(a.path.includes(focusOrderId)));
  }

  const focus = {};
  state.focus.forEach(({ route, value }, key) => {
    if (route !== pathname) return;
    // Bounded and masked like captured data: a wizard can hold a lot.
    const bounded = prepare({ path: '', data: prune(value) }, { isAdmin, cap });
    if (!isEmpty(bounded)) focus[key] = bounded;
  });
  if (focusOrderId) focus.order_id = focusOrderId;

  return compact({
    now: istTime(Date.now()),
    viewer: compact({
      role: truncate(state.viewer?.role, 40) || 'unknown',
      vendor: truncate(state.viewer?.vendor_name, 100),
    }),
    current_screen: compact({
      screen: screen?.name || 'Suspire Terminal',
      path: truncate(pathname, 200),
      view: viewFromUrl(),
      focus,
      data: current.map((e) => formatEntry(e, { isAdmin, cap, refs })),
    }),
    earlier_in_session: history.map((e) => formatEntry(e, {
      isAdmin,
      cap: Math.min(cap, HISTORY_LIST_CAP),
      refs,
      withScreen: true,
    })),
  });
};

// Order numbers, AWBs, SKUs: tokens in the question that contain a digit.
const referencesIn = (question) => (typeof question === 'string'
  ? Array.from(new Set((question.match(/#?[A-Za-z0-9][A-Za-z0-9-]{2,}/g) || [])
    .filter((token) => /\d/.test(token))
    .map((token) => token.replace(/^#/, '').toUpperCase())))
  : []);

/**
 * The Terminal snapshot sent to Tara with each question asked on a Terminal screen.
 * `question` puts the rows it mentions first. Async because it may first ask the
 * server for the viewer's role, which decides what has to be masked.
 */
export const buildTerminalTaraContext = async (question = '') => {
  ensureOwner();
  syncVisit();
  const owner = state.ownerId;

  if (!state.viewer && process.env.REACT_APP_API_BASE_URL) {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/shipturtle/v2/user/role/`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || localStorage.getItem('accessToken') || ''}`,
        },
      }).then((r) => (r.ok ? r.json() : null)).then((data) => {
        ensureOwner();
        if (state.ownerId === owner && data?.role) {
          state.viewer = { role: data.role, vendor_id: data.vendor_id, vendor_name: data.vendor_name };
        }
      });
    } catch {
      // Unknown role: everything role-gated stays masked.
    }
  }
  // Someone else signed in while the role was loading: this question is not theirs.
  ensureOwner();
  if (state.ownerId !== owner) return null;

  const pathname = window.location.pathname;
  const isAdmin = state.viewer?.role === 'admin';
  const focusOrderId = (pathname.match(new RegExp(`^/suspire-terminal/orders/(${UUID})`, 'i')) || [])[1];

  let snapshot = null;
  const refs = referencesIn(question);
  for (const cap of LIST_CAPS) {
    for (const historyLimit of [MAX_HISTORY_ENTRIES, 1, 0]) {
      snapshot = assemble({ cap, historyLimit, pathname, isAdmin, focusOrderId, refs });
      if (JSON.stringify(snapshot).length <= MAX_CONTEXT_CHARS) return snapshot;
      if (historyLimit === 0) break;
    }
  }
  // Still too big at the smallest caps: keep the newest current-screen data only.
  while (snapshot.current_screen?.data?.length > 1
    && JSON.stringify(snapshot).length > MAX_CONTEXT_CHARS) {
    snapshot.current_screen.data.pop();
  }
  return fitSnapshot(snapshot);
};

// Guarantees the bound: shrinks the URL view, focus and screen data in steps
// (they are already masked), and as a floor sends only what the screen is.
const fitSnapshot = (snapshot) => {
  const fits = () => JSON.stringify(snapshot).length <= MAX_CONTEXT_CHARS;
  if (fits()) return snapshot;
  const screen = snapshot.current_screen || {};
  for (const step of SHRINK_STEPS) {
    for (const part of ['view', 'focus', 'data']) {
      if (screen[part] !== undefined) screen[part] = shrink(screen[part], step);
      if (fits()) return snapshot;
    }
  }
  return compact({
    now: snapshot.now,
    viewer: snapshot.viewer,
    current_screen: compact({
      screen: screen.screen,
      path: screen.path,
      note: 'This screen holds more data than fits; ask the user to name the order, AWB or product.',
    }),
  });
};

/** Test hook: forget everything captured. */
export const resetTerminalContext = () => {
  state.entries.clear();
  state.focus.clear();
  state.viewer = null;
  state.ownerId = undefined;
  state.visit = 0;
  state.visitPath = null;
};
